import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, ExternalLink } from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState({ affiliate_url: '', site_name: '', qr_code_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getSettings().then(res => setSettings(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Einstellungen</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Affiliate-URL und Seiteneinstellungen</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
          data-testid="settings-save-button"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </div>

      <Card className="border-0 shadow-sm rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-body font-semibold text-slate-700">Affiliate-Konfiguration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="font-body text-sm">Affiliate-URL</Label>
            <p className="text-xs text-slate-400 font-body mb-1">Diese URL wird bei Klick auf "Jetzt Mietwagen buchen" verwendet</p>
            <Input
              value={settings.affiliate_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, affiliate_url: e.target.value }))}
              className="rounded-sm font-body font-mono text-xs"
              placeholder="https://..."
              data-testid="settings-affiliate-url-input"
            />
            {settings.affiliate_url && (
              <a
                href={settings.affiliate_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#0E518D] font-body inline-flex items-center gap-1 mt-2 hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> URL testen
              </a>
            )}
          </div>
          <div>
            <Label className="font-body text-sm">Seitenname</Label>
            <Input
              value={settings.site_name || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
              className="rounded-sm font-body"
              data-testid="settings-site-name-input"
            />
          </div>
          <div>
            <Label className="font-body text-sm">QR-Code Ziel-URL</Label>
            <p className="text-xs text-slate-400 font-body mb-1">URL, die im QR-Code kodiert wird</p>
            <Input
              value={settings.qr_code_url || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, qr_code_url: e.target.value }))}
              className="rounded-sm font-body"
              data-testid="settings-qr-url-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
